<script lang="ts">
  import FormInput from "../../common/form/FormInputWithSurroundError.svelte";
  import Icon from "../../common/icons/Icon.svelte";
  import NavigatorLinkWrapper from "../../common/link/NavigatorLinkWrapper.svelte";
  import Button from "../../common/form/Button.svelte";
  import AuthFormHeader from "../common/AuthFormHeader.svelte";
  import AuthFormStyle from "../common/AuthFormStyle.svelte";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-yup";
  import * as yup from "yup";
  import pageUrl from "../../../PageUrl";
  import { auth } from "../../../app/api";
  import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";

  const minPasswordLength = 6;
  let isFocus: boolean;
  let newPassword: string = "";
  let isSubmitting: boolean = false;
  let errorMessage: string = "";
  let requestErrorMessage: string = "";
  let isPasswordVisible: boolean = false;
  let resetPasswordSuccess: boolean = false;

  // Get the one-time code from the query parameter.
  const params = new URLSearchParams(document.location.search);
  const actionCode = params.get("oobCode");
  let isCodeValid: boolean = undefined;
  // check if the action code is valid
  checkCodeValid();

  async function checkCodeValid() {
    if (!actionCode) {
      isCodeValid = false;
      return;
    }
    try {
      const email = await verifyPasswordResetCode(auth, actionCode);
      isCodeValid = true;
    } catch (error) {
      isCodeValid = false;
    }
  }

  async function handleResetPassword() {
    await checkCodeValid();
    if (!isCodeValid) return;

    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      resetPasswordSuccess = true;
    } catch (error) {
      console.log("Reset password error: ", error);
      requestErrorMessage =
        "Couldn't reset your account password. Please try again.";
    }
  }

  const schema = yup.object({
    password: yup
      .string()
      .min(minPasswordLength, "That password is too short")
      .required("Please enter your password"),
  });

  const { form, errors, validate } = createForm({
    extend: validator({ schema }),
    onSubmit: async (values) => {
      if (isSubmitting) return;
      isSubmitting = true;
      updateErrorMessage();
      await handleResetPassword();
      isSubmitting = false;
    },
  });

  function updateErrorMessage() {
    let errs = $errors;
    errorMessage = errs.password ? errs.password[0] : "";
  }

  function togglePasswordVisible() {
    isPasswordVisible = !isPasswordVisible;
  }

  async function handleFormSubmit() {
    await validate();
    updateErrorMessage();
  }
</script>

<div class="auth-form-container" class:opacity-0={isCodeValid === undefined}>
  <div class="form-content">
    <AuthFormHeader title="Reset your password" />
    {#if isCodeValid}
      {#if !resetPasswordSuccess}
        <form use:form method="POST" on:submit|capture={handleFormSubmit}>
          <div class="description">Enter your new password</div>

          <div class="input-field">
            <p>Password</p>
            <div class="input-container" class:focus={isFocus}>
              <div class="prefix-icon">
                <Icon icon="input-password" width="20px" height="20px" />
              </div>

              <FormInput
                bind:value={newPassword}
                bind:isFocus
                {errorMessage}
                name="password"
                errorIndicatorIconSize="20px"
                type={isPasswordVisible ? "text" : "password"}
                showErrorIndicatorIcon={false}
                spellcheck="false"
                placeholder="Enter Your Password"
                autocomplete="off"
                disabled={isSubmitting}
              />

              <div class="postfix-icon">
                <button
                  type="button"
                  class="w-5 h-5"
                  on:click={togglePasswordVisible}
                >
                  <Icon
                    width="20px"
                    height="20px"
                    icon={isPasswordVisible
                      ? "password-visible"
                      : "password-invisible"}
                  />
                </button>
              </div>
            </div>

            {#if requestErrorMessage}
              <div class="failed-message">
                {requestErrorMessage}
              </div>
            {/if}
          </div>

          <div class="submit-button relative mt-[86px]">
            <Button disabled={isSubmitting}>Continue</Button>
          </div>
        </form>
      {:else}
        <div>
          <div class="success-message text-center mt-2">
            You can now sign in with your new password.
          </div>

          <div class="sign-in-now mt-3">
            <NavigatorLinkWrapper
              to={pageUrl.SIGNIN}
              textColor="#F918FF"
              hoverTextColor="#C418FF"
              activeTextColor="#C418FF"
            >
              <span class="font-semibold text-[15px]">Sign In Now</span>
              <Icon
                icon="splide-arrow-right"
                width="16px"
                height="11px"
                fill="none"
              />
            </NavigatorLinkWrapper>
          </div>
        </div>
      {/if}
    {:else if actionCode}
      <div class="mt-2">
        Your request to reset your password has expired or the link has already
        been used
      </div>
    {:else}
      <div class="mt-2">The reset password link is invalid</div>
    {/if}
  </div>
</div>

<AuthFormStyle />

<style lang="postcss">
  .form-content::before {
    bottom: 20px;
  }

  .description {
    @apply mt-6 mb-6 text-dark-50 text-[13px];
  }

  .submit-button :global(button) {
    @apply w-full text-[13px] py-3 px-0;
  }

  .failed-message {
    @apply absolute left-0 right-0 px-5 py-2.5 rounded-md text-[13px] font-medium text-tertiary-error bg-tertiary-error bg-opacity-10;
    top: calc(100% + 10px);
  }

  .sign-in-now :global(a) {
    @apply flex flex-row items-center justify-center gap-3;
    letter-spacing: 0px;
  }
</style>
